import { useEffect, useRef } from "react";
import { driver } from "driver.js";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { updateApplicationTourStatus } from "../services/ApiService";
import { UPDATE_USER_DATA } from "../redux/features/user/userSlice";
import { UPDATE_USER_TOUR } from "../redux/features/user/userTourSlice";

const ALL_TOUR_FLAGS = {
  welcome_page: true,
  main_dashboard: true,
  settings_page: true,
  programs_page: true,
  campaigns_page: true,
  integration_page: true,
  members_page: true,
  add_member_page: true,
};

const BUTTON_ID = {
  skip: "skipTourBtn",
  prev: "prevTourBtn",
  next: "nextTourBtn",
};

const SKIPPED_TOAST = "Tour skipped";

// Brief dwell after which we mark the current page's tour as "seen" even if
// the user has not yet pressed Next. Preserved from the prior per-page code.
const AUTO_MARK_SEEN_AFTER_MS = 18000;

const renderPopoverTitle = (title, current, total) =>
  `<div style="display:flex;justify-content:space-between;align-items:center;">
    <p style="margin-bottom:0rem">${title ?? ""}</p>
    <p style="margin-bottom:0rem;margin-right:2rem;">${current} of ${total}</p>
  </div>`;

const renderPopoverDescription = (description, isFirst) => `
  ${description ?? ""}
  <div style="margin-top:12px;display:flex;justify-content:space-between;gap:8px;">
    <button id="${BUTTON_ID.skip}" class="driver-custom-btn">Skip</button>
    <button id="${BUTTON_ID.prev}" class="driver-custom-btn ${isFirst ? "disabled btn-secondary" : ""}" ${isFirst ? "disabled style='opacity:0.5;cursor:not-allowed;'" : ""}> ← Previous</button>
    <button id="${BUTTON_ID.next}" class="driver-custom-btn">Next →</button>
  </div>`;

/**
 * Shared client tour driver.
 *
 * Centralises every page's `driver.js` tour configuration: the popover
 * template, the Skip/Previous/Next button wiring, modal-style behaviour
 * (outside clicks and Esc do not dismiss the tour), and the per-page
 * Redux flag updates. Individual pages stay declarative.
 *
 * Navigation contract:
 *   - "Next" on the final step  -> navigate(nextRoute)  (single-direction tour chain)
 *   - "Skip"                    -> ends entire tour, navigate(skipRoute)
 *   - Anywhere else (overlay, Esc, etc.) -> does nothing
 *
 * @param {object}   options
 * @param {boolean}  options.enabled    Whether the tour should run on mount.
 * @param {Array}    options.steps      driver.js step array for this page.
 * @param {string}   options.tourKey    Redux key in `state.tour` for this page.
 * @param {string}   options.nextRoute  Path navigated to from Next on last step.
 * @param {string}   options.skipRoute  Path navigated to from Skip / popover close.
 */
const useClientTour = ({ enabled, steps, tourKey, nextRoute, skipRoute }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user);

  // Latest user is captured in a ref so popover button handlers always see the
  // current value without re-binding (or re-running) the effect on every render.
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (!enabled || !Array.isArray(steps) || steps.length === 0) {
      return undefined;
    }

    const totalSteps = steps.length;
    const autoMarkTimers = [];

    const endTour = async () => {
      const currentUser = userRef.current;
      if (!currentUser) {
        console.error("User not available when ending the tour");
        return false;
      }
      const result = await updateApplicationTourStatus(currentUser);
      if (!result?.success) return false;
      dispatch(
        UPDATE_USER_DATA({ has_seen_tour: result?.data?.has_seen_tour }),
      );
      dispatch(UPDATE_USER_TOUR(ALL_TOUR_FLAGS));
      return true;
    };

    const driverObj = driver({
      popoverClass: "driverjs-theme driver-custom-controls",
      // Modal behavior: outside clicks and Esc must not dismiss the tour.
      // Tour ends only via Skip, Next-on-last-step, or finishing all steps.
      allowClose: false,
      onCloseClick: async () => {
        if (await endTour()) {
          driverObj.destroy();
          navigate(skipRoute);
          toast.info(SKIPPED_TOAST);
        }
      },
      steps: steps.map((step, index) => ({
        ...step,
        popover: {
          ...step.popover,
          title: renderPopoverTitle(
            step?.popover?.title,
            index + 1,
            totalSteps,
          ),
          description: renderPopoverDescription(
            step?.popover?.description,
            index === 0,
          ),
        },
      })),
      onPopoverRender: () => {
        autoMarkTimers.push(
          setTimeout(() => {
            dispatch(UPDATE_USER_TOUR({ [tourKey]: true }));
          }, AUTO_MARK_SEEN_AFTER_MS),
        );

        document
          .getElementById(BUTTON_ID.prev)
          ?.addEventListener("click", () => {
            driverObj.movePrevious();
          });

        document
          .getElementById(BUTTON_ID.next)
          ?.addEventListener("click", () => {
            if (driverObj.isLastStep()) {
              driverObj.destroy();
              navigate(nextRoute);
            } else {
              driverObj.moveNext();
            }
          });

        document
          .getElementById(BUTTON_ID.skip)
          ?.addEventListener("click", async () => {
            if (await endTour()) {
              driverObj.destroy();
              navigate(skipRoute);
              toast.info(SKIPPED_TOAST);
            }
          });
      },
      onDestroyed: () => {
        // Cleanup only. Navigation is owned by the Next-on-last-step and Skip
        // handlers to avoid chain-navigating on any other destruction path.
        dispatch(UPDATE_USER_TOUR({ [tourKey]: true }));
      },
    });

    driverObj.drive();

    // Mount-once setup. We intentionally do not react to subsequent changes in
    // `enabled` so Redux flag updates fired *by the tour itself* don't tear it
    // down mid-flight. Cleanup runs on unmount and disposes both the driver.js
    // DOM and any pending auto-mark timers.
    return () => {
      driverObj.destroy();
      autoMarkTimers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useClientTour;
